import { useEffect, useRef } from 'react';

import type { ResourceTypeHandle } from '@toolproof-core/genesis/_contract/_index';
import type { SpaceKey } from '@toolproof-core/visualization/_contract/key';
import type { CosmosData, SpaceDerivationOutput } from '@toolproof-core/visualization/derivation/_contract/derivation';

type NativeAdapterDriverObserverProps = {
	spaceKey: SpaceKey;
	cosmosData: CosmosData<ResourceTypeHandle>;
	derivationOutput: SpaceDerivationOutput;
	error: Error | null;
	enabled: boolean;
};

export function NativeAdapterDriverObserver(props: NativeAdapterDriverObserverProps) {
	const { spaceKey, cosmosData, derivationOutput, error, enabled } = props;
	const lastLogSignatureRef = useRef<string>('');

	useEffect(() => {
		if (error) {
			console.error(`[NativeAdapterDriver:${spaceKey}] cosmosData fetch error`, error);
			return;
		}
		if (!enabled) {
			lastLogSignatureRef.current = '';
			return;
		}

		const cosmosSummary = Object.fromEntries(
			Object.entries(cosmosData.resourcesByType).map(([resourceTypeHandle, resources]) => [
				resourceTypeHandle,
				{
					total: (resources ?? []).length,
					hydrated: (resources ?? []).filter((resource) => resource.projection !== null).length,
				},
			])
		);

		const derivationSummary = {
			itemCollectionCount: derivationOutput.base.itemCollections.length,
			itemCount: derivationOutput.base.itemCollections.reduce((sum, itemCollection) => sum + itemCollection.items.length, 0),
			constellationCount: derivationOutput.base.constellations.length,
			transitionPhaseCount: derivationOutput.transitionPhases?.length ?? 0,
			transitionPhaseKeys: derivationOutput.transitionPhases?.map((phase) =>
				phase.transitionOptions.transitionKey ?? phase.transitionOptions.transitionKind
			) ?? [],
		};

		const signature = JSON.stringify({
			cosmosSummary,
			derivationSummary,
		});

		if (signature === lastLogSignatureRef.current) return;
		lastLogSignatureRef.current = signature;

		console.debug(`[NativeAdapterDriver:${spaceKey}] derivation diagnostics`, derivationSummary);
	}, [cosmosData, derivationOutput, error, spaceKey, enabled]);

	return null;
}
